<template>
  <div>
    <v-row>
      <v-col md="12">
        <v-form ref="ingredientForm" v-model="form.valid">
          <v-container grid-list-xl>
            <v-layout wrap>
              <v-flex md6>
                <div>
                  <v-combobox
                    data-cy="product-name"
                    v-if="!form.meta.product.disabled"
                    v-model="form.data.product"
                    :items="products.data"
                    :loading="products.loading"
                    :search-input.sync="products.search"
                    :rules="form.rules.product"
                    dense
                    outlined
                    rounded
                    hide-selected
                    hide-no-data
                    item-text="name"
                    item-value="uuid"
                    :label="$t('ingredients.labels.product')"
                    :placeholder="$t('ingredients.writeAName')"
                    @input.native="search"
                    @change="productSelected"
                    ref="product"
                    return-object>
                    <template v-slot:item="{ item }">
                      <div>
                        {{ item.name }}
                        <v-chip
                          v-if="item.subrecipe"
                          x-small>
                          Subreceta
                        </v-chip>
                      </div>
                    </template>
                  </v-combobox>
                  <v-text-field
                    v-else
                    dense
                    outlined
                    rounded
                    :value="form.data.product ? form.data.product.name : ''"
                    :label="$t('ingredients.labels.product')"
                    :disabled="form.meta.product.disabled"
                    />
                </div>
                <span
                  v-if="products.finishSearch && !products.data.length"
                  class="d-flex justify-center mb-1"
                  style="color: red; font-size: smaller;">
                  No se encontraron coincidencias, elige una opción diferente
                </span>
              </v-flex>
              <v-flex md6>
                <v-select
                  ref="providers"
                  v-model="form.data.provider"
                  :items="providers.data"
                  item-text="name"
                  item-value="uuid"
                  :disabled="form.meta.provider.disabled"
                  :label="$t('ingredients.labels.provider')"
                  no-data-text="No hay proveedores de este producto"
                  placeholder="Selecciona un proveedor"
                  @change="selectProviderByProduct"
                  @keyup.up="activateProvidersMenu"
                  @keyup.down="activateProvidersMenu"
                  dense
                  outlined
                  rounded
                  return-object>
                  <template v-slot:item="{ item }">
                    <div style="width: 100%">
                      <div>
                        {{ item.name }}
                      </div>
                      <div class="d-flex justify-space-between">
                        <div class="body-2">
                          {{ item.display }} en ${{ item.unitPrice | toFixNumber(2) | numberWithComma }}
                          <span v-if="item.brand">
                            | {{ item.brand }}
                          </span>
                        </div>
                      </div>
                      <div>
                        <div class="body-2">
                          ${{ item.price | toFixNumber(2) | numberWithComma }} x {{ item.unit }}
                        </div>
                      </div>
                    </div>
                  </template>
                </v-select>
              </v-flex>
              <v-flex md6>
                <v-text-field
                  data-cy="product-quantity"
                  :label="$t('ingredients.labels.quantity')"
                  placeholder="Ingresa la que se necesita"
                  v-model.number="form.data.quantity"
                  :rules="form.rules.quantity"
                  type="number"
                  dense
                  outlined
                  rounded
                  required>
                </v-text-field>
              </v-flex>
              <v-flex md6>
                <v-select
                ref="units"
                :label="$t('ingredients.labels.unit')"
                v-model="form.data.unit"
                :items="form.meta.units.data"
                :rules="form.rules.unit"
                :disabled="form.meta.units.disabled"
                placeholder="Selecciona una unidad"
                item-text="name"
                item-value="uuid"
                @keyup.up="activateUnitsMenu"
                @keyup.down="activateUnitsMenu"
                dense
                outlined
                rounded
                return-object
                required>
                <template v-slot:item="{ item }">
                  <div>
                    {{ item.name }} <span class="caption grey--text">{{ convertUnit(item) }}</span>
                  </div>
                </template>
                </v-select>
              </v-flex>
            </v-layout>
            <div class="d-flex justify-end">
              <v-btn
                class="mr-3"
                color="primary"
                outlined
                rounded
                small
                depressed
                @click="resetForm">
                Cancelar
              </v-btn>
              <v-btn
                data-cy="product-save"
                color="primary"
                rounded
                small
                depressed
                @click="submit">
                {{ $t('ingredients.actions.save') }}
              </v-btn>
            </div>
          </v-container>
        </v-form>
      </v-col>
      <v-col md="12">
        <v-data-table
          :loading-text="table.title"
          :server-items-length="table.total"
          :loading="table.loading"
          :headers="table.headers"
          :items="table.data"
          :items-per-page="table.options.itemsPerPage"
          :footer-props="{
            'items-per-page-options': [15, 20, 30]
          }"
          :page="table.options.page"
          @update:options="updateTable"
          dense
          class="elevation-1">
          <template v-slot:item.quantity="{ item }">
            <div class="text-right">
              {{ item.quantity | toFixNumber(3) | numberWithComma }}
            </div>
          </template>
          <template
            v-slot:item.name="{ item }">
            <div class="price-cell">
              <v-tooltip right>
                <template v-slot:activator="{ on }">
                  <span v-on="on">
                    {{ item.name }}
                    <v-icon v-if="item.choice === null || (item.choice && item.choice.provider === null)" color="red" small>mdi-alert</v-icon>
                    <div v-else>
                      <div class="grey--text" v-if="item.choice">
                        {{ item.product.category.name }}
                      </div>
                      <div :class="item.onModel === 'Recipe' ? 'grey--text' : 'red--text'" v-else>
                        {{ item.onModel === 'Recipe' ? 'Subrecetas' : 'Error en la variante' }}
                      </div>
                    </div>
                  </span>
                </template>
                <div v-if="item.choice === null || (item.choice && item.choice.provider === null)">
                  <span>Este ingrediente ya no está disponible, por favor elige uno nuevo</span>
                </div>
                <div v-else>
                    <div
                    v-if="item.choice"
                    style="width: 100%">
                    ${{ item.choice.amount | toFix2 | numberWithComma }} x {{ item.choice.unit.code }}
                  </div>
                  <div
                    v-else
                    style="width: 100%">
                    ${{ (item.product || {}).unitCost || 0 | toFix2 | numberWithComma }} x {{ item.unit.code }}
                  </div>
                </div>
              </v-tooltip>
            </div>
          </template>
          <template v-slot:item.price="{ item }">
            <div class="text-right">
              ${{ item.price | toFix2 | numberWithComma }}
            </div>
          </template>
          <template v-slot:item.action="{ item }">
            <v-icon small class="mr-2" @click="openUpdateDialog(item)">mdi-border-color</v-icon>
            <v-icon small color="error" @click="openRemoveDialog(item)">mdi-delete</v-icon>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog @keydown.esc="removeDialog=false" v-model="removeDialog" persistent max-width="400px">
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('ingredients.actions.remove', { name: ingredientToRemove.name }) }}</span>
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="black darken-1" text @click="onCloseRemoveDialog">{{ $t('messages.no') }}</v-btn>
          <v-btn color="black darken-1" text @click="remove">{{ $t('messages.yes') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { cloneDeep, debounce } from 'lodash'
import RecipeModel from '@/models/Recipe'

export default {
  props: {
    recipe: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    let recipeModel = new RecipeModel()

    return {
      recipeModel,
      removeDialog: false,
      ingredientToRemove: {},
      table: {
        title: this.$i18n.t('ingredients.loading'),
        loading: true,
        headers: [
          { text: this.$i18n.t('ingredients.headers.name'), value: 'name' },
          { text: this.$i18n.t('ingredients.headers.quantity'), value: 'quantity' },
          { text: this.$i18n.t('ingredients.headers.baseUnit'), value: 'unit.name' },
          { text: this.$i18n.t('ingredients.headers.price'), value: 'price' },
          { text: this.$i18n.t('ingredients.headers.actions'), value: 'action', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 15,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: ['name'],
          sortDesc: [false]
        }
      },
      form: {
        valid: false,
        data: {
          unit: null,
          quantity: 0,
          recipe: null,
          product: null,
          provider: null,
          choice: null
        },
        rules: {
          product: [v => !!v || this.$t('validations.product')],
          name: [v => !!v || this.$t('validations.name')],
          unit: [v => !!v || this.$t('validations.unit')],
          quantity: [v => !!v || this.$t('validations.quantity')]
        },
        meta: {
          product: { disabled: false },
          provider: { disabled: true },
          units: { disabled: true, data: [] }
        }
      },
      providers: {
        loading: false,
        data: [],
        total: 0
      },
      products: {
        finishSearch: false,
        search: '',
        loading: false,
        data: [],
        total: 0
      },
      selectedRecipe: {
        name: '',
        cost: ''
      }
    }
  },
  computed: {
    units: function () {
      const unitsAllowed = [
        'm',
        'cm',
        'g',
        'kg',
        'oz',
        'l',
        'ml',
        'glass',
        'cup',
        'cop',
        'oza',
        'ozi',
        'teaspoon',
        'tablespoon',
        'pz',
        'unit'
      ]
      return this.$store.state.login.units.data.filter(unit => unitsAllowed.includes(unit.code)) || []
    }
  },
  watch: {
    recipe: async function (val) {
      if (val) {
        this.$refs.ingredientForm.reset()
        const recipe = cloneDeep(val)
        this.recipeModel = new RecipeModel(recipe)
        this.selectedRecipe = val
        this.form.data = {
          unit: null,
          quantity: 0,
          recipe: cloneDeep(val),
          product: null,
          provider: null
        }
        this.form.meta.product.disabled = false
      }
      if (val.uuid) await this.load()
    }
  },
  methods: {
    search: debounce(async function (event) {
      const val = event.target.value
      this.products.finishSearch = false
      if (val) {
        await this.searchProduct(val)
      } else {
        this.products.data = []
        this.products.finishSearch = false
      }
      this.products.loading = false
    }, 600),
    searchProduct: async function (name) {
      this.products.loading = true
      const params = { name: `/${name}/i`, skip: 0, limit: 20 }
      const recipeParams = {
        subrecipe: true,
        status: 'attached',
        area: this.$store.state.area.id,
        '_id!': `${this.recipe.uuid}`,
        ...params
      }
      const [products, subrecipes] = await Promise.all([
        this.$api.get('/v1/products', params),
        this.$api.get('/v1/recipes', recipeParams)
      ])
      this.products.data = products.data
        .map(product => {
          if (product.restaurant) {
            product.name = `${product.name} | Sugerido`
          }
          return product
        })
        .concat(subrecipes.data)
      this.products.total = products.total + subrecipes.total
      this.products.finishSearch = true
    },
    async findAndSelect (product) {
      this.products.search = product.name
      this.products.data = [product]
      this.products.finishSearch = false
      this.form.data.product = product
      this.productSelected(product)
    },
    convertUnit (unit) {
      const baseUnit = this.units.find(u => u.magnitude === unit.magnitude && u.value === 1)
      const converted = (baseUnit.value !== unit.value)
        ? `${(baseUnit.value * unit.value).toFixed(3)} ${baseUnit.name}`
        : ''
      return `${converted}`
    },
    activateUnitsMenu () {
      this.$refs.units.activateMenu()
    },
    activateProvidersMenu () {
      this.$refs.providers.activateMenu()
    },
    updateTable: async function (data) {
      if (this.recipe.uuid) {
        this.table.options = data
        await this.load()
      }
    },
    async loadRecipe () {
      const { response } = await this.recipeModel.detail()
      this.recipeModel = new RecipeModel(response.data)
      this.selectedRecipe = response.data
    },
    load: async function (params = null) {
      params = params || {
        sort: `${this.table.options.sortDesc[0] ? '-' : ''}${this.table.options.sortBy[0]}`,
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1))

      }
      this.table.loading = true
      try {
        const response = await this
          .fetch(`recipes/${this.recipe.uuid}/ingredients`, params)
        this.table.data = response.data
        this.table.total = response.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    openUpdateDialog: async function (ingredient) {
      try {
        await this.productSelected(ingredient.product)
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.form.data = cloneDeep(ingredient)
      this.form.data.recipe = cloneDeep(this.recipe)
      if (ingredient.choice) {
        this.form.data.provider = ingredient.choice
        this.form.data.choice = ingredient.choice.uuid
        this.providerSelected(ingredient.choice.unit)
        this.form.meta.provider.disabled = false
      }
      this.form.meta.product.disabled = true
    },
    resetForm () {
      this.form.meta.product.disabled = false
      this.form.data = {
        unit: null,
        quantity: 0,
        recipe: null,
        product: null,
        provider: null
      }
      this.table.options.itemsPerPage = 15
      this.$refs.ingredientForm.reset()
    },
    reset () {
      this.ingredientToRemove = {}
      this.resetForm()
      this.form.meta.provider.disabled = true
      this.form.meta.units.disabled = true
      this.table.options.itemsPerPage = 15
    },
    openRemoveDialog (ingredient) {
      this.ingredientToRemove = cloneDeep(ingredient)
      this.removeDialog = true
    },
    onCloseRemoveDialog () {
      this.ingredientToRemove = {}
      this.removeDialog = false
    },
    fetch: async function (entity, params) {
      return this.$api.get(`/v1/${entity}`, params)
    },
    find (entity, id) {
      return this.$api.get(`/v1/${entity}/${id}`)
    },
    submit: async function () {
      if (this.$refs.ingredientForm.validate()) {
        const request = this.form.data.uuid == null
          ? this.create()
          : this.update()
        this.overlay = true
        try {
          await request
          await this.load()
          this.$refs.ingredientForm.reset()
          this.form.data = { recipe: cloneDeep(this.recipe) }
          this.$toasted.show('La receta se modificó correctamente', { duration: 3000 })
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
      }
      this.resetForm()
      this.form.meta.product.disabled = false
      this.form.meta.units.disabled = false
      await this.loadRecipe()
      this.$emit('updated')
    },
    create () {
      const payload = cloneDeep(this.form.data)
      payload.choice = payload.provider ? payload.provider.uuid : null
      if (payload.provider == null) {
        payload.subrecipe = payload.product
      }

      if (payload.unit) {
        payload.unit = payload.unit.uuid
      }

      if (payload.product.subrecipe) {
        return this.$api.post(`/v1/recipes/${this.recipe.uuid}/ingredients`, {
          subrecipe: true,
          quantity: payload.quantity,
          unit: payload.unit,
          product: payload.product.uuid
        })
      } else {
        delete payload.provider

        return this.$api.post(`/v1/recipes/${this.recipe.uuid}/ingredients`, {
          choice: payload.choice,
          quantity: payload.quantity,
          unit: payload.unit,
          product: payload.product.uuid
        })
      }
    },
    async update () {
      const form = cloneDeep(this.form.data)
      const payload = {
        uuid: form.uuid,
        unit: form.unit.uuid,
        choice: form.choice,
        quantity: form.quantity
      }

      try {
        await this.$api.post(`/v1/recipes/${this.recipe.uuid}/ingredients/${payload.uuid}`, payload)
        await this.loadRecipe()
        this.$emit('updated')
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    },
    remove: async function () {
      try {
        await this.$api
          .post(`/v1/recipes/${this.recipe.uuid}/ingredients/${this.ingredientToRemove.uuid}/delete`)
        this.$refs.ingredientForm.reset()
        await this.load()
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.removeDialog = false
      await this.loadRecipe()
      this.$emit('updated')
    },
    productSelected: async function (product) {
      if (product) {
        this.providers.data = []
        this.providers.total = 0
        this.form.meta.provider.disabled = true
        if (product.uuid) {
          if (!product.subrecipe) {
            const response = await this.$api.get(`/v1/products/${product.uuid}`)
            if (response.choices) {
              this.providers.data = response.choices.map(x => {
                let display = `${x.display.name}`
                if (x.display.code !== x.unit.code) {
                  display = display.concat(` de ${x.contentQuantity} ${x.unit.code}`)
                }
                return {
                  name: x.provider.name,
                  brand: x.brand,
                  uuid: x.uuid,
                  price: this.calculatePriceProductWithTaxes(x) / x.contentQuantity,
                  unitPrice: this.calculatePriceProductWithTaxes(x),
                  unit: x.unit.name,
                  magnitude: x.unit.magnitude,
                  unitId: x.unit.id,
                  display
                }
              })
              this.providers.total = response.choices.length
              this.form.meta.provider.disabled = false
            }
          } else {
            this.form.meta.units.data = this.units.filter(x => x.magnitude === product.unitStock.magnitude)
          }
          if (product.subrecipe) {
            this.form.meta.units.disabled = false
          }
        }
      }
    },
    selectProviderByProduct (item) {
      this.form.data.choice = item.uuid
      this.providerSelected(item)
    },
    providerSelected (unit) {
      if (unit) {
        if (unit.magnitude === 'custom') {
          const customUnit = unit.unit
            ? {
              name: unit.unit,
              id: unit.unitId,
              uuid: unit.unitId
            }
            : unit
          this.form.data.unit = customUnit
          this.form.meta.units.data = [customUnit]
          this.form.meta.units.disabled = true
        } else {
          this.form.meta.units.data = this.units.filter(x => x.magnitude === unit.magnitude)
          this.form.meta.units.disabled = false
        }
      }
    },
    calculatePriceProductWithTaxes (product) {
      const hasBaseAmount = product.taxes.every(tax => tax.baseAmount)

      if (hasBaseAmount) {
        const taxesAmount = product.taxes.reduce((acc, tax) => {
          const taxBase = tax.isQuota ? tax.base : (tax.base / 100)
          if (tax.isRetention) {
            return acc - (tax.baseAmount * taxBase)
          }
          return acc + (tax.baseAmount * taxBase)
        }, 0)

        return product.amount + taxesAmount
      } else {
        let priceWithTaxes = product.amount
        const iva = product.taxes.find(tax => tax.satCode === '002' && tax.base > 0)
        const taxesInQuota = product.taxes.filter(tax => tax.isQuota && tax.satCode !== '002')
        const taxesNoQuota = product.taxes.filter(tax => !tax.isQuota && tax.satCode !== '002')

        const amountTaxInQuota = taxesInQuota.map(tax => {
          return tax.base
        })

        const amountTaxNoQuota = taxesNoQuota.map(tax => {
          return (tax.base / 100) * priceWithTaxes
        })

        priceWithTaxes = amountTaxInQuota.reduce((acc, tax) => {
          return acc + tax
        }, priceWithTaxes)

        priceWithTaxes = amountTaxNoQuota.reduce((acc, tax) => {
          return acc + tax
        }, priceWithTaxes)

        if (iva) {
          priceWithTaxes += (iva.base / 100) * priceWithTaxes
        }

        return priceWithTaxes
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.price{
  &-cell {
    position: relative;
  }

  &-tooltip {
    top: 0 !important;
    opacity: 1 !important;
    background: black !important;
  }
}
</style>
